import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Round Tablecloth — per Quoting_markdown/tablecloths.md: Diameter and
// Height/Drop drive Cut Length; Fabric Width (default 54") and Pattern
// Repeat (optional) affect cut count / yardage — see calcTableclothRound,
// sopCalculations.ts. widthInches carries Diameter, heightInches carries
// Height/Drop, reusing the generic geometry columns.
//
// Unlike most item types, this form's values are submitted through dedicated
// addTableclothRoundDraftQuoteObject/updateTableclothRoundDraftQuoteObject
// mutations (see DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject)
// rather than the generic addDraftQuoteObject/updateDraftQuoteObject every
// non-dedicated-resolver type shares.
const TableclothRoundForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [diameter, setDiameter] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [drop, setDrop] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [fabricWidthInches, setFabricWidthInches] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeatInches, setPatternRepeatInches] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const d = parseFloat(diameter);
      if (!d || d <= 0) {
        setError('Diameter must be greater than 0');
        return null;
      }
      const h = parseFloat(drop);
      if (!h || h <= 0) {
        setError('Height/Drop must be greater than 0');
        return null;
      }
      setError(null);
      return {
        widthInches: d,
        heightInches: h,
        fabricWidthInches: fabricWidthInches ? parseFloat(fabricWidthInches) : 54,
        patternRepeatInches: patternRepeatInches ? parseFloat(patternRepeatInches) : null,
        fabricDescription: fabricDescription.trim() || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Diameter (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={diameter}
            onChange={(e) => setDiameter(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Height / Drop (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={drop}
            onChange={(e) => setDrop(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={fabricWidthInches}
            onChange={(e) => setFabricWidthInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Pattern Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            placeholder="Leave blank if no repeat"
            value={patternRepeatInches}
            onChange={(e) => setPatternRepeatInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name, item) => {
              setFabricDescription(name);
              if (item?.widthInches) {
                setFabricWidthInches(String(item.widthInches));
              }
            }}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default TableclothRoundForm;
